/**
 * Grafiki w szablonach dokumentów (docs/25): logo firmy wystawiającej i grafiki wgrane do szablonu (podpis,
 * pieczęć, baner). W treści szablonu grafika to <img data-grafika="<id pliku>" data-rozmiar="m">; przy tworzeniu
 * dokumentu obraz trafia do treści jako dane (data:image/…), więc PDF i zamrożona treść umowy nie odwołują się
 * do żadnych adresów. Dozwolone wyłącznie PNG i JPG do 1 MB.
 */

export const DOC_IMAGE_MAX_BYTES = 1024 * 1024;
export const DOC_IMAGE_MIME = ['image/png', 'image/jpeg'] as const;
export type DocImageMime = (typeof DOC_IMAGE_MIME)[number];

/** Pole szablonu z logo firmy wystawiającej dokument (wstawiane w akapicie — wyrównanie jak tekstu). */
export const TEMPLATE_LOGO_KEY = 'firma.logo';
/** Warunek „firma ma logo” (np. {{#if firma.ma_logo}}…{{/if}}). */
export const TEMPLATE_HAS_LOGO_KEY = 'firma.ma_logo';

export const DOC_IMAGE_SIZES = { s: 'Mała (25%)', m: 'Średnia (45%)', l: 'Duża (70%)', pelna: 'Pełna szerokość' } as const;
export type DocImageSize = keyof typeof DOC_IMAGE_SIZES;
export const DOC_IMAGE_WIDTH: Record<DocImageSize, string> = { s: '25%', m: '45%', l: '70%', pelna: '100%' };

/** Adres grafiki w edytorze (tylko podgląd w aplikacji — do dokumentu trafia treść pliku). */
export const docImageUrl = (fileId: string) => `/api/templates/images/${fileId}`;

const IMG_RE = /<img\b[^>]*>/gi;
const ID_RE = /\bdata-grafika\s*=\s*["']([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})["']/i;
const SIZE_RE = /\bdata-rozmiar\s*=\s*["'](s|m|l|pelna)["']/i;
const DATA_URI_RE = /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/]+=*$/;

/** Rozpoznaje typ obrazu po zawartości (nie po nazwie ani deklaracji przeglądarki). */
export function sniffDocImage(bytes: Uint8Array): DocImageMime | null {
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  return null;
}

export const isDocImageDataUri = (s: string | null | undefined): s is string => !!s && DATA_URI_RE.test(s);

/** Identyfikatory grafik użytych w treści szablonu. */
export function documentImageIds(html: string): string[] {
  const ids = new Set<string>();
  for (const tag of html.match(IMG_RE) ?? []) {
    const id = ID_RE.exec(tag)?.[1];
    if (id) ids.add(id.toLowerCase());
  }
  return [...ids];
}

/**
 * Zapis szablonu: grafiki w postaci kanonicznej (id + rozmiar, adres podglądu), pozostałe <img> usunięte
 * (np. obrazy z wklejonego HTML albo adresy zewnętrzne).
 */
export function normalizeDocumentImages(html: string): string {
  return html.replace(IMG_RE, (tag) => {
    const id = ID_RE.exec(tag)?.[1]?.toLowerCase();
    if (!id) return '';
    const size = (SIZE_RE.exec(tag)?.[1]?.toLowerCase() ?? 'm') as DocImageSize;
    // kolejność atrybutów jak w edytorze (RichEditor) — zapis bez zmian nie tworzy nowej wersji
    return `<img data-grafika="${id}" data-rozmiar="${size}" src="${docImageUrl(id)}" style="width: ${DOC_IMAGE_WIDTH[size]}" alt="">`;
  });
}

/**
 * Dokument: każdą grafikę z treści szablonu zastępuje obrazem w treści (data:image/png|jpeg). Grafika nieznana
 * (resolve → null) i każdy inny <img> są usuwane. Stosować do treści szablonu przed wypełnieniem pól.
 */
export function inlineDocumentImages(html: string, resolve: (fileId: string) => string | null | undefined): string {
  return html.replace(IMG_RE, (tag) => {
    const id = ID_RE.exec(tag)?.[1]?.toLowerCase();
    const src = id ? resolve(id) : null;
    if (!isDocImageDataUri(src)) return '';
    const size = (SIZE_RE.exec(tag)?.[1]?.toLowerCase() ?? 'm') as DocImageSize;
    return `<img class="dok-grafika" src="${src}" style="width:${DOC_IMAGE_WIDTH[size]}" alt="">`;
  });
}

/** Logo firmy do pola {{firma.logo}} (pusty tekst, gdy firma nie ma logo). */
export function logoImg(src: string | null | undefined): string {
  return isDocImageDataUri(src) ? `<img class="dok-logo" src="${src}" alt="Logo">` : '';
}

/** Pola logo dla danych szablonu: blok {{firma.logo}} i warunek {{#if firma.ma_logo}}. */
export function logoInput(src: string | null | undefined): { field: Record<string, string>; block: Record<string, string> } {
  const img = logoImg(src);
  return { field: { [TEMPLATE_HAS_LOGO_KEY]: img ? 'tak' : '' }, block: { [TEMPLATE_LOGO_KEY]: img } };
}
